import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $profileFeatureStore, $usersStore } from '@lib/store';
import { useListState, useRouteHook, useView, ViewEnum } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import type { UsersRequest } from '@shared/api';
import { AddNewUserModal, SuccessDeletedUserModal, User, UserListView } from './widgets';
import { PageContainer, View } from '@widgets';
import { Button, Icon, Pagination, Spinner } from '@shared/widgets';
import PlusIcon from '@assets/icons/plus.svg';
import GridViewIcon from '@assets/icons/grid-view.svg';
import LineViewIcon from '@assets/icons/line-view.svg';

export const UserManagementPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { navigate } = useRouteHook();

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const usersStore = useUnit($usersStore.store);

  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showSuccessDeletedUserModal, setShowSuccessDeletedUserModal] = useState<boolean>(false);
  const usersState = useListState({ list: 'users' });
  const viewState = useView({ page: 'users' });

  const { t } = useTranslation('userManagement');

  useEffect(() => {
    const show = searchParams.get('successDeleted');
    setShowSuccessDeletedUserModal(show === 'true');
    const params = new URLSearchParams(searchParams);
    params.delete('successDeleted');
    setSearchParams(params);

    return () => $usersStore.events.clear();
  }, []);

  const params: UsersRequest = useMemo(() => ({ FromIndex: usersState.fromIndex, Count: usersState.count, ReturnTotalCount: true }), [usersState]);
  useEffect(() => {
    if (!profileFeatureStore.data?.userManagement?.viewUsers) {
      navigate(RoutePaths.dashboard.onNavigate());
      return;
    }

    if (profileFeatureStore.data?.userManagement?.managerAccountsOrCardsPermissions) $usersStore.events.getUsers(params);
    else $usersStore.events.getUsersWithoutPermission(params);
  }, [usersState]);

  if (!profileFeatureStore.data?.userManagement?.viewUsers) return null;
  return (
    <PageContainer
      title={t('title')}
      header={t('header', { count: usersStore.data.total })}
      topRightAddons={
        <div className='flex items-center gap-2'>
          {profileFeatureStore.data?.userManagement?.createUser && (
            <Button className='mr-3' color='primary' onClick={() => setShowAddModal(true)}>
              <Icon icon={<PlusIcon />} color='inherit' />
              {t('buttons.add', { ns: 'common' })}
            </Button>
          )}
          <Button
            className={cn('h-10 w-10 rounded-sm', PaletteUtils.isVcfcPalette && 'bg-light/10', 'hover:bg-light/60 dark:hover:bg-light/30')}
            color='icon'
            onClick={() => viewState.onChangeView(ViewEnum.Grid)}
          >
            <Icon
              className={cn(
                PaletteUtils.isInsigniaPalette && [viewState.isGrid && '!text-primary'],
                PaletteUtils.isVcfcPalette && ['dark:!text-white/50', viewState.isGrid && '!text-primary dark:!text-white'],
              )}
              icon={<GridViewIcon />}
            />
          </Button>
          <Button
            className={cn('h-10 w-10 rounded-sm', PaletteUtils.isVcfcPalette && 'bg-light/10', 'hover:bg-light/60 dark:hover:bg-light/30')}
            color='icon'
            onClick={() => viewState.onChangeView(ViewEnum.List)}
          >
            <Icon
              className={cn(
                PaletteUtils.isInsigniaPalette && [viewState.isList && '!text-primary'],
                PaletteUtils.isVcfcPalette && ['dark:!text-white/50', viewState.isList && '!text-primary dark:!text-white'],
              )}
              icon={<LineViewIcon />}
            />
          </Button>
        </div>
      }
    >
      <View
        loading={usersStore.initialLoading}
        error={usersStore.error}
        noData={!usersStore.data.data.length}
        onRefresh={() =>
          profileFeatureStore.data?.userManagement?.managerAccountsOrCardsPermissions
            ? $usersStore.events.getUsers(params)
            : $usersStore.events.getUsersWithoutPermission(params)
        }
      >
        {usersStore.loading && <Spinner position />}
        {profileFeatureStore.data?.userManagement?.viewUsers && (
          <section className='flex flex-col gap-6'>
            <div className={cn('grid gap-4', viewState.isList ? 'grid-cols-1' : 'grid-cols-3', PaletteUtils.isVcfcPalette && 'max-md:grid-cols-1')}>
              {viewState.isGrid && usersStore.data.data.map((user, idx) => <User key={idx} user={user} />)}
              {viewState.isList && <UserListView users={usersStore.data.data} />}
            </div>
            <Pagination
              totalItemCount={usersStore.data.total}
              currentPage={usersState.page}
              countItemPage={usersState.count}
              onChangePage={usersState.onChangePage}
              onChangeCountItemPage={usersState.onChangeCount}
              perPageLabel={viewState.isGrid ? t('pagination.perPage.items', { ns: 'common' }) : undefined}
            />
          </section>
        )}
      </View>
      <AddNewUserModal show={showAddModal} onClose={() => setShowAddModal(false)} params={params} />
      <SuccessDeletedUserModal show={showSuccessDeletedUserModal} onClose={() => setShowSuccessDeletedUserModal(false)} />
    </PageContainer>
  );
};
